<template>
  <section style="width: 100%">
    <p :style="{
      color: '#111111',
      fontSize: isMobile ? '14px' : '18px',
      fontWeight: 700,
      letterSpacing: isMobile ? '4px' : '6px',
      lineHeight: 1.25,
      margin: isMobile ? '0 0 22px' : '0 0 28px',
      textTransform: 'uppercase',
    }">
      WEALTH LENDING NETWORK
    </p>
    <h1 :style="{
      color: '#001f5a',
      fontSize: isMobile ? '48px' : 'clamp(54px, 8vw, 92px)',
      fontWeight: 400,
      lineHeight: 1.08,
      margin: '0 0 24px',
    }">
      Connect Clients to <strong>Liquidity</strong>
    </h1>
    <p :style="{
      color: '#686868',
      fontSize: isMobile ? '22px' : 'clamp(24px, 3vw, 31px)',
      fontWeight: 400,
      lineHeight: 1.3,
      margin: '0 0 36px',
    }">
      Your network to access lending for your clients
    </p>

    <div style="margin-top: 10px" aria-live="polite">
      <div :style="{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        gap: '12px',
        marginBottom: '18px',
      }"><span :style="{
        background: '#c9d1df',
        display: 'block',
        flex: 1,
        height: '1px',
      }"></span>
        <h2 style="
            color: #5f5f5f;
            font-size: 22px;
            font-weight: 500;
            line-height: 1.2;
            margin: 0;
            white-space: nowrap;
          ">
          Partner Banks
        </h2>
        <span :style="{
          background: '#c9d1df',
          display: 'block',
          flex: 1,
          height: '1px',
        }"></span>
      </div>

      <div v-if="banksLoading" style="
          align-items: center;
          background: #ffffff;
          border: 1px solid #dfe4ed;
          border-radius: 8px;
          color: #686868;
          display: flex;
          min-height: 104px;
          padding: 24px;
        ">
        Loading partner banks...
      </div>
      <div v-else-if="banksError" style="
          align-items: center;
          background: #ffffff;
          border: 1px solid #d7a6a6;
          border-radius: 8px;
          color: #a12626;
          display: flex;
          min-height: 104px;
          padding: 24px;
        ">
        {{ banksError }}
      </div>
      <div v-else style="
          display: grid;
          gap: 18px;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
        ">
        <a v-for="(bank, index) in partnerBanks" :key="index" :href="resolveS3Url(bank.href)" target="_blank"
          rel="noopener noreferrer" :aria-label="`Visit ${bank.name}`" :style="getCardStyle(index)"
          @mouseenter="setActiveCard(index)" @mouseleave="clearActiveCard(index)" @focus="setActiveCard(index)"
          @blur="clearActiveCard(index)">
          <img :src="resolveS3Url(bank.logoSrc)" :alt="bank.name" :style="getLogoStyle(bank)" />
        </a>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  data() {
    return {
      isMobile: false,
      activeCardIndex: null,
      partnerBanks: [],
      banksLoading: false,
      banksError: null,
    }
  },
  computed: {
    partnerBanksAssetsDomain() {
      return process.env.VUE_APP_PARTNER_BANKS_ASSETS_DOMAIN;
    }
  },
  mounted() {
    this.updateViewport();
    window.addEventListener("resize", this.updateViewport);
    this.fetchPartnerBanks();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateViewport);
  },
  methods: {
    setActiveCard(index) {
      this.activeCardIndex = index;
    },
    clearActiveCard(index) {
      if (this.activeCardIndex === index) {
        this.activeCardIndex = null;
      }
    },
    getCardStyle(index) {
      const isActive = this.activeCardIndex === index;

      return {
        alignItems: "center",
        background: "#ffffff",
        border: `1px solid ${isActive ? "#9faec6" : "#dfe4ed"}`,
        borderRadius: "8px",
        boxShadow: isActive
          ? "0 6px 14px rgba(0, 31, 90, 0.10)"
          : "0 2px 8px rgba(0, 31, 90, 0.06)",
        display: "flex",
        justifyContent: "center",
        minHeight: "128px",
        padding: "24px",
        transform: isActive ? "translateY(-1px)" : "translateY(0)",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
      };
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    async fetchPartnerBanks() {
      try {
        this.banksLoading = true;
        const response = await this.$http.get(
          `${this.partnerBanksAssetsDomain}/partnerBanks.json`
        );
        this.partnerBanks = response.data;
        this.banksLoading = false;
      } catch (error) {
        this.banksError = "Failed to load partner banks";
        this.banksLoading = false;
      }
    },
    resolveS3Url(path) {
      // If path starts with http(s), return as is, else prepend custom domain
      if (/^https?:\/\//i.test(path)) {
        return path;
      }
      return `${this.partnerBanksAssetsDomain}/${path.replace(/^\/+/, "")}`;
    },
    parseCssSizeNumber(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
      }

      if (typeof value !== "string") {
        return null;
      }

      const trimmed = value.trim();
      // Match a number with optional decimal and optional "px" suffix, case-insensitive
      const match = /^(-?\d+(?:\.\d+)?)(?:px)?$/i.exec(trimmed);
      if (!match) {
        return null;
      }

      const numericValue = Number(match[1]);
      return Number.isFinite(numericValue) ? numericValue : null;
    },
    capCssSize(value, maxPx) {
      const numericValue = this.parseCssSizeNumber(value);
      return `${Math.min(numericValue ?? Number.MAX_SAFE_INTEGER, maxPx)}px`;
    },
    getLogoStyle(bank) {
      const width = this.capCssSize(bank.width, 300);
      const height = this.capCssSize(bank.height, 200);

      return {
        maxWidth: width,
        maxHeight: height,
        width: "auto",
        height: "auto",
        display: "block",
        objectFit: "contain",
      };
    },
  }
};
</script>
